<script lang="ts">
import { OrderEventType } from '@/modules/marketplace/types/marketplace';
import { computed, shallowRef, watch } from 'vue';
import { getEventTypeColor } from '@/modules/marketplace/helpers/marketplace';
import Decimal from 'decimal.js';
import { AgTableClient } from '@/modules/common/components/ag-table';
import { ColDef, SortModelItem } from 'ag-grid-enterprise';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace-order-history';
import { OmsHistoryItem, OmsHistoryResponse, OmsOrderSummary } from '@/modules/marketplace/models';

const sort: SortModelItem = { colId: 'eventTimestamp', sort: 'desc' };

const defaultSelectedEvents: OrderEventType[] = [
  'CREATED',
  'MODIFIED',
  'CANCELED',
  'EXPIRED',
  'EXECUTED',
  'CLOSED',
  'TERMINATED',
  'SPLIT_EXECUTED',
  'SPLIT_CANCELED',
  'FORCE_CANCELED',
  'SPLIT_FAILED',
];

/*
 * 1) Always highlight all cells of the first event (length - 1)
 * 2) Highlight if current event cell value is different from previous event
 */
export function isDifferent(
  previousRow: OmsOrderSummary,
  currentRow: OmsOrderSummary,
  name: string
): boolean {
  if (name === 'openQuantity') {
    // calculate openQuantity and compare
    return currentRow.quantity - currentRow.filled !== previousRow.quantity - previousRow.filled;
  }

  const previousCell = previousRow[name];
  const currentCell = currentRow[name];

  if (
    (previousCell === null && currentCell !== null) ||
    (previousCell !== null && currentCell === null)
  ) {
    return true;
  }

  if (previousCell instanceof Decimal) {
    return !previousCell.eq(currentCell);
  }

  if (typeof previousCell === 'object') {
    return previousCell?.companyId !== currentCell?.companyId;
  }

  return previousCell !== currentCell;
}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{ details: OmsHistoryResponse | null; isInsideDialog?: boolean }>(),
  { isInsideDialog: false }
);

const emit = defineEmits<{ (e: 'open-loan-details', loanId: string | null): void }>();

/*
 * Because the component stays mounted, we need to reset the "selectedEvents"
 * (v-chip-group automatically unselects items not present in "filterableEvents",
 * resulting in  stale "selectedEvents" when the parent component changes the "details")
 */
watch(
  () => props.details,
  () => {
    selectedEvents.value = [...defaultSelectedEvents];
  }
);

const selectedEvents = shallowRef<OrderEventType[]>([...defaultSelectedEvents]);
const unfilterableEventTypes: OrderEventType[] = ['CREATED'];

const filteredHistory = computed(
  () =>
    props.details?.history
      .filter((i) => selectedEvents.value.includes(i.eventType))
      .sort((a, b) => +b.eventTimestamp - +a.eventTimestamp) ?? []
);

/*
 * UI only displays eventType that can be filtered
 * Return a list of unique eventType based on the fetched history data
 */
const filterableEvents = computed(() =>
  props.details?.history.reduce<OrderEventType[]>((filterable, event) => {
    if (
      !filterable.includes(event.eventType) &&
      !unfilterableEventTypes.includes(event.eventType)
    ) {
      filterable.push(event.eventType);
    }
    return filterable;
  }, [])
);

const allColumnDefs = computed(() => {
  const config = { cellHighlight };
  return [
    cols.eventTimestamp(),
    cols.eventType({
      openLoanDetails: (loanId: string | null) => emit('open-loan-details', loanId),
    }),
    cols.quantity(config),
    cols.openQuantity(config),
    cols.ioiQuantity(config),
    cols.firmQuantity(config),
    cols.filled(config),
    cols.fee(config),
    cols.rebate(config),
    cols.avgExecutionFee(config),
    cols.avgExecutionRebate(config),
    cols.agreements(config),
    cols.minQuantity(config),
    cols.orderType(config),
    cols.timeInForceType(config),
    cols.initiator(),
  ];
});

const columnDefs = computed<Array<ColDef<OmsHistoryItem>>>(() => {
  const visibleCols = new Set([
    'eventTimestamp',
    'eventType',
    'routingStatus',
    'quantity',
    'openQuantity',
    'filled',
    'rate',
    'avgExecutionFee',
    'avgExecutionRebate',
    'agreements',
    'initiator',
  ]);

  const columnDefs: Array<ColDef<OmsHistoryItem>> = [];
  for (const colDef of allColumnDefs.value) {
    const colId = colDef.colId!;

    if (visibleCols.has(colId)) {
      columnDefs.push(colDef);
      continue;
    }

    // other columns are only displayed if there there are multiple values between rows
    for (let i = 1; i < filteredHistory.value.length; ++i) {
      let valPrev = filteredHistory.value[i - 1].summary[colId];
      let valCur = filteredHistory.value[i].summary[colId];
      valPrev = valPrev instanceof Decimal ? valPrev.toString() : valPrev;
      valCur = valCur instanceof Decimal ? valCur.toString() : valCur;
      if (valPrev !== valCur) {
        columnDefs.push(colDef);
        break;
      }
    }
  }

  return columnDefs;
});

/*
 * 1) Always highlight all cells of the first event (length - 1)
 * 2) Highlight if current event cell value is different from previous event
 */
function cellHighlight(name: string, index: number): 'highlight' | '' {
  if (index === filteredHistory.value.length - 1) {
    return 'highlight';
  }

  // previous item is index + 1 (list is reversed)
  const previousRow = filteredHistory.value[index + 1].summary;
  const currentRow = filteredHistory.value[index].summary;

  return isDifferent(previousRow, currentRow, name) ? 'highlight' : '';
}

function getRowId(omsHistoryItem: OmsHistoryItem): string {
  return omsHistoryItem.eventIndex.toString();
}
</script>

<template>
  <div style="display: contents">
    <AgTableClient
      :auto-grow="isInsideDialog"
      :column-defs="columnDefs"
      :get-row-id="getRowId"
      no-menu
      no-move
      :row-data="filteredHistory"
      :sort="sort"
    />

    <!-- Filter by eventType -->
    <VRow class="mt-4 filter-by" style="margin: 0">
      <VCol class="col-12 d-flex align-center justify-end">
        <span v-if="filterableEvents?.length" class="text--primary mr-2">Filter by</span>
        <VChipGroup v-model="selectedEvents" column multiple>
          <VChip
            v-for="eventType in filterableEvents"
            :key="eventType"
            :color="getEventTypeColor(eventType)"
            filter
            outlined
            small
            :value="eventType"
          >
            {{ $t(`orderHistory.eventType.${eventType}`) }}
          </VChip>
        </VChipGroup>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .v-card {
    min-height: 60vh;
  }

  .ag-header-row {
    --ag-header-foreground-color: rgba(255, 255, 255, 0.7);
  }

  .ag-row,
  .summary {
    /* Tone down grey to increase contrast between bold and non-bold text */
    color: #888;
  }

  .ag-row .highlight,
  .summary strong {
    color: white;
    font-weight: bold;
  }
}

.filter-by {
  background-color: #1e1e1e;
  position: sticky;
  bottom: 0;

  .theme--light & {
    background: white;
  }
}

.tooltip-icon {
  margin-left: 6px;
}
</style>
